import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { addPasskey, changePassword, listPasskeys, removePasskey, type PasskeyInfo } from '../lib/account';
import { ApiError } from '../lib/api';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';

/**
 * The operator's own account (backlog 507). Two things every staff member needs before
 * go-live: change the interim `test1234` password, and manage their passkeys — add a second
 * device, remove a lost one. The last passkey cannot be removed (the server refuses it too),
 * or the account locks itself out under the passkey rule.
 */
export function Account() {
  const { identity } = useAuth();
  const { notify } = useToast();

  return (
    <div className="page">
      <h1 className="page__title">Your account</h1>
      {identity && (
        <p className="page__muted">
          Signed in as {identity.email ?? `user #${identity.userId}`} · signed in with {identity.method}.
        </p>
      )}
      <div className="settings__list">
        <PasswordCard onNotify={notify} />
        <PasskeysCard onNotify={notify} />
      </div>
    </div>
  );
}

type Notify = ReturnType<typeof useToast>['notify'];

function PasswordCard({ onNotify }: { onNotify: Notify }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = current !== '' && next.length >= 8 && next === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      await changePassword(current, next);
      onNotify({ variant: 'success', message: 'Password changed.' });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      onNotify({ variant: 'error', message: err instanceof ApiError ? err.message : 'Could not change the password.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="settings__row" onSubmit={submit}>
      <div className="settings__meta">
        <code className="settings__key">Password</code>
        <p className="settings__desc">Change your sign-in password. At least 8 characters, and different from the current one.</p>
      </div>
      <div className="account__form">
        <input className="settings__input" type="password" placeholder="Current password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} disabled={busy} />
        <input className="settings__input" type="password" placeholder="New password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} disabled={busy} />
        <input className="settings__input" type="password" placeholder="Confirm new password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy} />
        {confirm !== '' && next !== confirm && <p className="settings__stamp settings__stamp--warn">The new passwords do not match.</p>}
        <button type="submit" className="settings__save" disabled={!ready || busy}>{busy ? 'Changing…' : 'Change password'}</button>
      </div>
    </form>
  );
}

function PasskeysCard({ onNotify }: { onNotify: Notify }) {
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listPasskeys().then(setPasskeys).catch(() => setPasskeys([]));
  }, []);

  async function add() {
    if (busy) return;
    setBusy(true);
    try {
      const os = navigator.userAgent;
      const label = /iPhone|iPad/.test(os) ? 'iOS' : /Android/.test(os) ? 'Android' : /Mac/.test(os) ? 'macOS' : /Windows/.test(os) ? 'Windows' : 'device';
      setPasskeys(await addPasskey(`${label} · ${new Date().toISOString().slice(0, 10)}`));
      onNotify({ variant: 'success', message: 'Passkey added.' });
    } catch (err) {
      onNotify({ variant: 'error', message: err instanceof ApiError ? err.message : 'The passkey was not added (cancelled or unsupported).' });
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (busy) return;
    setBusy(true);
    try {
      setPasskeys(await removePasskey(id));
      onNotify({ variant: 'success', message: 'Passkey removed.' });
    } catch (err) {
      onNotify({ variant: 'error', message: err instanceof ApiError ? err.message : 'Could not remove the passkey.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__row">
      <div className="settings__meta">
        <code className="settings__key">Passkeys</code>
        <p className="settings__desc">The devices that can confirm your sign-in. Add another before you switch machines; you cannot remove your only one.</p>
      </div>
      <div className="account__form">
        {passkeys === null ? (
          <p className="settings__stamp">Loading…</p>
        ) : passkeys.length === 0 ? (
          <p className="settings__stamp">No passkeys on this account.</p>
        ) : (
          <ul className="account__passkeys">
            {passkeys.map((p) => (
              <li key={p.id} className="account__passkey">
                <span>
                  <strong>{p.name ?? `Passkey #${p.id}`}</strong>
                  <span className="settings__stamp">
                    {' '}added {new Date(p.createdAt).toLocaleDateString()}
                    {p.lastUsedAt ? ` · last used ${new Date(p.lastUsedAt).toLocaleDateString()}` : ' · not used yet'}
                  </span>
                </span>
                <button type="button" className="settings__save settings__save--danger" disabled={busy || passkeys.length <= 1} onClick={() => remove(p.id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="settings__save" disabled={busy} onClick={add}>
          {busy ? 'Working…' : 'Add a passkey'}
        </button>
      </div>
    </div>
  );
}
